import { useEffect, useState } from 'react';

type OptionProps<T> = {
  /**
   * The delay in milliseconds before unsetting the state.
   * Default is `1000ms` (1 second).
   */
  delay?: number;
  setCondition?: (item: T) => boolean;
};

/**
 * Custom hook that delays unsetting a state value when it becomes falsy.
 *
 * @template T - The type of the state item.
 * @param item - The value to be managed with delayed unsetting.
 * @param options - Configuration options for the delay duration.
 * @returns An object containing the current state item.
 *
 * @example
 * ```tsx
 * const { item } = useDelayedUnsetState(value, { delay: 2000 });
 * ```
 */
export const useDelayedUnsetState = <T>(item: T, options?: OptionProps<T>) => {
  const { delay = 1000, setCondition } = options ?? {};
  const [_item, _setItem] = useState<T | null>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (setCondition) {
      const isSet = setCondition(item);
      if (isSet) _setItem(item);
      else {
        timeout = setTimeout(() => _setItem(null), delay);
      }
    } else if (item) {
      _setItem(item);
    } else {
      timeout = setTimeout(() => _setItem(null), delay);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [item]);

  return {
    item: _item,
  };
};
